.form {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
}

.formActions {
  display: flex;
  justify-content: flex-end;
  gap: var(--space-3);
  margin-top: var(--space-2);
  padding-top: var(--space-4);
  border-top: 1px solid var(--border);
}

.formRow {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--space-4);
}

@media (max-width: 560px) {
  .formRow { grid-template-columns: 1fr; }
}

/* ----------------------------------------------------------- table cells */
.primaryText {
  font-weight: var(--weight-medium);
  color: var(--text);
}

.secondaryText {
  margin-top: 2px;
  font-size: var(--text-xs);
  color: var(--text-muted);
  /* Descriptions can be long; keep rows a predictable height. */
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  line-clamp: 2;
  overflow: hidden;
  max-width: 46ch;
}

.rowActions {
  display: flex;
  justify-content: flex-end;
  gap: var(--space-1);
}

.mono {
  font-family: var(--font-mono);
  font-size: var(--text-xs);
  color: var(--text-muted);
}

/* ------------------------------------------------------------- preview */
/*
  Live calculation shown beside a form.

  Pricing and fee rules are the two screens where the FIELDS do not tell you
  the OUTCOME — compounding changes the total in a way nobody computes in their
  head. The preview is the point of these screens, not decoration.
*/
.preview {
  padding: var(--space-4);
  background: var(--bg-sunken);
  border: 1px solid var(--border);
  border-radius: var(--radius);
}

.previewTitle {
  font-size: var(--text-xs);
  font-weight: var(--weight-semibold);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--text-muted);
  margin-bottom: var(--space-3);
}

.previewLine {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: var(--space-4);
  padding: var(--space-2) 0;
  font-size: var(--text-sm);
  border-bottom: 1px dashed var(--border);
}

.previewLine:last-of-type { border-bottom: none; }

.previewLabel { color: var(--text-muted); }

.previewValue {
  font-weight: var(--weight-medium);
  white-space: nowrap;
}

.previewTotal {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: var(--space-4);
  margin-top: var(--space-2);
  padding-top: var(--space-3);
  border-top: 2px solid var(--border-strong);
  font-weight: var(--weight-semibold);
}

.previewTotalValue {
  font-size: var(--text-lg);
  color: var(--indigo);
  white-space: nowrap;
}

.previewNote {
  margin-top: var(--space-3);
  font-size: var(--text-xs);
  color: var(--text-muted);
  line-height: var(--leading-normal);
}

/* --------------------------------------------------------------- policy */
.policyForm {
  display: flex;
  flex-direction: column;
  gap: var(--space-5);
  max-width: 640px;
}

.policySection {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
  padding-bottom: var(--space-5);
  border-bottom: 1px solid var(--border);
}

.policySection:last-of-type {
  border-bottom: none;
  padding-bottom: 0;
}

.policySectionTitle {
  font-size: var(--text-sm);
  font-weight: var(--weight-semibold);
  color: var(--text);
}

.policySectionHint {
  font-size: var(--text-xs);
  color: var(--text-muted);
  line-height: var(--leading-normal);
  margin-top: 2px;
}

/* Radio-style mode picker: three mutually exclusive behaviours where the
   consequence of each needs spelling out. */
.modeOptions {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.modeOption {
  display: flex;
  align-items: flex-start;
  gap: var(--space-3);
  padding: var(--space-3);
  border: 1px solid var(--border-strong);
  border-radius: var(--radius);
  cursor: pointer;
  transition: border-color var(--transition-fast), background var(--transition-fast);
}

.modeOption:hover { background: var(--bg-hover); }

.modeOption:has(input:checked) {
  border-color: var(--indigo);
  background: var(--indigo-soft);
}

.modeRadio {
  margin-top: 2px;
  accent-color: var(--indigo);
  flex-shrink: 0;
}

/*
  Label and description are <span>s inside a wrapping <span>, so they must be
  made block-level explicitly. Left inline, they run together on one line:
  "Always availableStudents can message any mentor…".
*/
.modeLabel {
  display: block;
  font-weight: var(--weight-medium);
  color: var(--text);
}

.modeDescription {
  display: block;
  margin-top: 2px;
  font-size: var(--text-xs);
  color: var(--text-muted);
  line-height: var(--leading-normal);
}

.saveBar {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: var(--space-3);
  padding-top: var(--space-4);
  border-top: 1px solid var(--border);
}

.savedNote {
  font-size: var(--text-sm);
  color: var(--text-muted);
  margin-right: auto;
}

/* --------------------------------------------------------- rules list */
.rulesList {
  margin: 0;
  padding-left: var(--space-5);
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  color: var(--text);
  font-size: var(--text-sm);
}

.rulesList li::marker {
  color: var(--indigo);
  font-weight: var(--weight-semibold);
}

/* *** THE ACTION ROW MUST NOT SCROLL AWAY ***
   Every form renders its buttons inside the modal body rather than the
   Modal's own footer slot, so on a long form they scrolled out of reach —
   the "Add mentor" dialog showed a clipped Cancel/Save. Sticking the row to
   the bottom of the scroll container keeps them reachable without having to
   restructure each page.

   The negative margins let the sticky bar span the body's full width, so its
   background covers the content sliding underneath rather than leaving a gap. */
.formActions {
  position: sticky;
  bottom: calc(var(--space-5) * -1);
  z-index: 1;
  margin: var(--space-2) calc(var(--space-5) * -1) calc(var(--space-5) * -1);
  padding: var(--space-4) var(--space-5);
  background: var(--bg-sunken);
  border-top: 1px solid var(--border);
}

@media (max-width: 480px) {
  .formActions {
    flex-direction: column-reverse;
  }
  .formActions > * {
    width: 100%;
  }
}
